import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils/cn'
import { jumpTo } from '@/shell/forms/jump'
import type { SopDetail, SopTemplate } from '../api/types'
import { formatSopDate, prepareSopHtml, type SopHeading } from '../utils/sop-html'
import { sopDownloadUrl } from '../utils/sop-url'
import './sop-document.css'

export interface SopDocumentViewProps {
  sop: SopDetail
  template: SopTemplate | undefined
  /** SOP Management shows empty template sections so authors see the gaps;
   *  the library hides them. */
  showEmptySections?: boolean
}

interface RenderedSection {
  id: string
  key: string
  number: number
  label: string
  html: string
  headings: SopHeading[]
  required: boolean
}

/** The SOP as a document: document-control block, numbered template
 *  sections, and an "On this page" list that follows the scroll. */
export function SopDocumentView({ sop, template, showEmptySections = false }: SopDocumentViewProps) {
  const sections = useMemo<RenderedSection[]>(() => {
    if (sop.sections && template) {
      const out: RenderedSection[] = []
      for (const s of template.sections) {
        const prepared = prepareSopHtml(sop.sections[s.key], `sop-${s.key}`)
        if (!prepared.html && !showEmptySections) continue
        out.push({
          id: `sop-section-${s.key}`,
          key: s.key,
          number: out.length + 1,
          label: s.label,
          html: prepared.html,
          headings: prepared.headings,
          required: s.required,
        })
      }
      return out
    }
    // Free-form SOP (not converted to the template yet): one body.
    const prepared = prepareSopHtml(sop.body_html, 'sop-body', 'h1, h2, h3')
    return prepared.html
      ? [{ id: 'sop-section-body', key: 'body', number: 0, label: '', html: prepared.html, headings: prepared.headings, required: false }]
      : []
  }, [sop.sections, sop.body_html, template, showEmptySections])

  const tocIds = useMemo(
    () => sections.flatMap((s) => (s.number ? [s.id, ...s.headings.map((h) => h.id)] : s.headings.map((h) => h.id))),
    [sections],
  )
  const active = useActiveHeading(tocIds)

  return (
    <div className="mx-auto flex w-full max-w-[1180px] items-start gap-8">
      <aside className="sticky top-0 hidden w-[260px] shrink-0 lg:block">
        <nav aria-label="On this page" className="flex max-h-[calc(100vh-12rem)] flex-col gap-0.5 overflow-auto pb-4">
          <div className="text-role-section-label mb-2 px-3">On this page</div>
          {sections.map((s) => (
            <div key={s.id} className="flex flex-col">
              {s.number ? (
                <TocLink id={s.id} active={active === s.id} strong>
                  <span className="w-5 shrink-0 text-tertiary">{s.number}</span>
                  <span className={cn(!s.html && 'text-tertiary')}>{s.label}</span>
                </TocLink>
              ) : null}
              {s.headings.map((h) => (
                <TocLink key={h.id} id={h.id} active={active === h.id} indent={s.number ? 1 : h.level >= 3 ? 1 : 0}>
                  {h.text}
                </TocLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <article className="min-w-0 flex-1 rounded-lg border border-border-default bg-surface shadow-sm">
        <DocumentControl sop={sop} highlightMissing={showEmptySections} />
        <div className="flex flex-col px-10 pb-12 pt-2">
          {sections.length === 0 ? (
            <p className="py-10 text-center text-md text-secondary">This SOP has no content yet.</p>
          ) : null}
          {sections.map((s) =>
            s.number ? (
              <section key={s.id} id={s.id} aria-labelledby={`${s.id}-title`} className="scroll-mt-4 border-t border-border-subtle pt-8 first:border-t-0 [&:not(:first-child)]:mt-8">
                <h2 id={`${s.id}-title`} className="mb-4 flex items-center gap-3 text-2xl font-semibold leading-tight text-primary">
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-md font-semibold text-accent-text">
                    {s.number}
                  </span>
                  {s.label}
                </h2>
                {s.html ? (
                  // Sanitized in prepareSopHtml (shared DOMPurify allowlist).
                  <div className="sop-doc" dangerouslySetInnerHTML={{ __html: s.html }} />
                ) : (
                  <p className={cn('rounded-md border border-dashed px-4 py-3 text-md',
                    s.required ? 'border-warning-border bg-warning-bg text-warning-text' : 'border-border-default text-secondary')}>
                    {s.required ? 'Required by the SOP template and not written yet.' : 'Not used in this SOP.'}
                  </p>
                )}
              </section>
            ) : (
              <div key={s.id} className="sop-doc pt-6" dangerouslySetInnerHTML={{ __html: s.html }} />
            ),
          )}
        </div>
      </article>
    </div>
  )
}

function TocLink({
  id,
  active,
  strong,
  indent = 0,
  children,
}: {
  id: string
  active: boolean
  strong?: boolean
  indent?: number
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => jumpTo(id)}
      aria-current={active ? 'location' : undefined}
      className={cn(
        'flex items-baseline gap-1 rounded-md border-l-2 py-1.5 pr-2 text-left leading-snug hover:bg-surface-hover',
        indent ? 'pl-8 text-sm' : 'pl-3 text-md',
        strong ? 'font-medium text-primary' : 'text-secondary',
        active ? 'border-accent bg-accent-subtle text-accent-text' : 'border-transparent',
      )}
    >
      {children}
    </button>
  )
}

const HEADER_ROWS: { key: keyof SopDetail; label: string; date?: boolean; required: boolean }[] = [
  { key: 'department', label: 'Department', required: true },
  { key: 'owner', label: 'Owner', required: true },
  { key: 'approved_by', label: 'Approved by', required: true },
  { key: 'version', label: 'Version', required: true },
  { key: 'effective_date', label: 'Effective date', date: true, required: true },
  { key: 'next_review_date', label: 'Next review', date: true, required: false },
]

/** The template's document-control header (Style Guide §2 and §7). */
export function DocumentControl({ sop, highlightMissing }: { sop: SopDetail; highlightMissing?: boolean }) {
  return (
    <dl className="grid grid-cols-2 gap-x-8 gap-y-4 border-b border-border-subtle bg-surface-sunk px-10 py-6 sm:grid-cols-3">
      {HEADER_ROWS.map((row) => {
        const raw = sop[row.key] as string | null
        const value = row.date ? formatSopDate(raw) : raw
        const missing = !value && row.required
        return (
          <div key={row.key} className="flex min-w-0 flex-col gap-1">
            <dt className="text-role-section-label">{row.label}</dt>
            <dd className={cn('text-md', value ? 'text-primary' : highlightMissing && missing ? 'font-medium text-warning-text' : 'text-tertiary')}>
              {value || (highlightMissing && missing ? 'Missing' : '—')}
            </dd>
          </div>
        )
      })}
      {sop.has_pdf && sop.is_template ? (
        <div className="col-span-full flex min-w-0 flex-col gap-1">
          <dt className="text-role-section-label">Attached for reference</dt>
          <dd className="text-md">
            <a href={sopDownloadUrl(sop.id)} target="_blank" rel="noreferrer" className="text-link underline underline-offset-2 hover:text-link-hover">
              Original PDF
            </a>
          </dd>
        </div>
      ) : null}
    </dl>
  )
}

/** Id of the heading nearest the top of the reading area. */
function useActiveHeading(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null)
  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === 'undefined') return
    const visible = new Map<string, number>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top)
          else visible.delete(e.target.id)
        }
        const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0]
        if (top) setActive(top[0])
      },
      { rootMargin: '0px 0px -65% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [ids])
  return active
}
